import { type BookmarkedPage, type BookmarkedPages, OoxmlRange } from "@common/utils/redline/ooxml-range";

export interface MergeWrittenPackage {
    replacedPackageOoxml: string;
    mergedPackageOoxml: string;
}

interface MergeWrittenPackageHolder {
    bookmarkNames: ReadonlySet<string>;
    mergedPackageOoxml: string;
    pagesOfMergedPackage: BookmarkedPages | null;
}

export type BulkApplyBodyReadAnswer = { covered: true; page: BookmarkedPage } | { covered: false };

const NOT_COVERED_BY_BODY_READ: BulkApplyBodyReadAnswer = { covered: false };

const BOOKMARK_START_TAG = /<w:bookmarkStart\b[^>]*>/g;
const BOOKMARK_END_TAG = /<w:bookmarkEnd\b[^>]*>/g;
const MARKER_ID_ATTRIBUTE = /\bw:id="([^"]*)"/;
const MARKER_NAME_ATTRIBUTE = /\bw:name="([^"]*)"/;

interface BookmarkStartMarker {
    id: string | undefined;
    name: string;
}

const bookmarkStartMarkersIn = (packageOoxml: string): BookmarkStartMarker[] =>
    Array.from(packageOoxml.matchAll(BOOKMARK_START_TAG), ([startTag]) => ({ id: MARKER_ID_ATTRIBUTE.exec(startTag)?.[1], name: MARKER_NAME_ATTRIBUTE.exec(startTag)?.[1] }))
        .filter((marker): marker is BookmarkStartMarker => marker.name !== undefined);

const bookmarkStartNamesIn = (packageOoxml: string): string[] =>
    bookmarkStartMarkersIn(packageOoxml).map(({ name }) => name);

const bookmarkNamesHoldingBothMarkersIn = (packageOoxml: string): Set<string> => {
    const endIds = new Set(Array.from(packageOoxml.matchAll(BOOKMARK_END_TAG), ([endTag]) => MARKER_ID_ATTRIBUTE.exec(endTag)?.[1]));
    return new Set(bookmarkStartMarkersIn(packageOoxml).filter(({ id }) => id !== undefined && endIds.has(id)).map(({ name }) => name));
};

export class BulkApplyBodyRead {
    private pagesOfBodyExport: BookmarkedPages | null = null;
    private mergeWrittenPackages: MergeWrittenPackageHolder[] = [];
    private bookmarkNamesWrittenByMerges = new Set<string>();
    private bookmarkNamesWrittenWithoutPackage = new Set<string>();

    get holdsBodyExport(): boolean {
        return this.pagesOfBodyExport !== null;
    }

    startFromBodyExport(pagesOfBodyExport: BookmarkedPages): void {
        this.pagesOfBodyExport = pagesOfBodyExport;
        this.mergeWrittenPackages = [];
        this.bookmarkNamesWrittenByMerges = new Set();
        this.bookmarkNamesWrittenWithoutPackage = new Set();
    }

    recordMergeWrittenPackages(writtenPackages: MergeWrittenPackage[]): void {
        for (const { replacedPackageOoxml, mergedPackageOoxml } of writtenPackages) {
            const bookmarkNames = new Set([...bookmarkStartNamesIn(replacedPackageOoxml), ...bookmarkStartNamesIn(mergedPackageOoxml)]);
            this.mergeWrittenPackages.push({ bookmarkNames, mergedPackageOoxml, pagesOfMergedPackage: null });
            bookmarkNames.forEach((bookmarkName) => this.bookmarkNamesWrittenByMerges.add(bookmarkName));
        }
    }

    recordWritesWithoutPackage(bookmarkNames: string[]): void {
        bookmarkNames.forEach((bookmarkName) => this.bookmarkNamesWrittenWithoutPackage.add(bookmarkName));
        const pageOfWrittenParagraphs = this.pagesOfBodyExport?.pageOf(bookmarkNames) ?? null;
        if (pageOfWrittenParagraphs?.kind !== "page") return;
        bookmarkStartNamesIn(pageOfWrittenParagraphs.packageOoxml).forEach((bookmarkName) => this.bookmarkNamesWrittenWithoutPackage.add(bookmarkName));
    }

    pageAfterMerges(bookmarkNames: string[]): BulkApplyBodyReadAnswer {
        if (this.pagesOfBodyExport === null) return NOT_COVERED_BY_BODY_READ;
        if (bookmarkNames.some((bookmarkName) => this.bookmarkNamesWrittenWithoutPackage.has(bookmarkName))) return NOT_COVERED_BY_BODY_READ;
        const holdersTouchingTheBookmarks = this.mergeWrittenPackages.filter((holder) => bookmarkNames.some((bookmarkName) => holder.bookmarkNames.has(bookmarkName)));
        if (holdersTouchingTheBookmarks.length === 0) return this.pageFromBodyExport(bookmarkNames, this.pagesOfBodyExport);
        const [onlyHolder] = holdersTouchingTheBookmarks;
        const onlyHolderWroteEveryBookmark = holdersTouchingTheBookmarks.length === 1 && bookmarkNames.every((bookmarkName) => onlyHolder.bookmarkNames.has(bookmarkName));
        if (!onlyHolderWroteEveryBookmark) return NOT_COVERED_BY_BODY_READ;
        onlyHolder.pagesOfMergedPackage ??= OoxmlRange.bookmarkedPagesOf(onlyHolder.mergedPackageOoxml);
        const pageOfMergedPackage = onlyHolder.pagesOfMergedPackage.pageOf(bookmarkNames);
        return pageOfMergedPackage === null ? NOT_COVERED_BY_BODY_READ : this.answerWhenPageHoldsEveryBookmark(bookmarkNames, pageOfMergedPackage);
    }

    private answerWhenPageHoldsEveryBookmark(bookmarkNames: string[], page: BookmarkedPage): BulkApplyBodyReadAnswer {
        if (page.kind === "readFromWord") return { covered: true, page };
        const bookmarkNamesHoldingBothMarkers = bookmarkNamesHoldingBothMarkersIn(page.packageOoxml);
        return bookmarkNames.every((bookmarkName) => bookmarkNamesHoldingBothMarkers.has(bookmarkName)) ? { covered: true, page } : NOT_COVERED_BY_BODY_READ;
    }

    private pageFromBodyExport(bookmarkNames: string[], pagesOfBodyExport: BookmarkedPages): BulkApplyBodyReadAnswer {
        const pageOfBodyExport = pagesOfBodyExport.pageOf(bookmarkNames);
        if (pageOfBodyExport === null) return NOT_COVERED_BY_BODY_READ;
        if (pageOfBodyExport.kind === "readFromWord") return { covered: true, page: pageOfBodyExport };
        const pageHoldsAWrittenParagraph = bookmarkStartNamesIn(pageOfBodyExport.packageOoxml).some(
            (bookmarkNameInPage) => this.bookmarkNamesWrittenByMerges.has(bookmarkNameInPage) || this.bookmarkNamesWrittenWithoutPackage.has(bookmarkNameInPage),
        );
        return pageHoldsAWrittenParagraph ? NOT_COVERED_BY_BODY_READ : this.answerWhenPageHoldsEveryBookmark(bookmarkNames, pageOfBodyExport);
    }
}
